import { useState, useEffect, useCallback, useRef } from 'react';

const COLS = 40;
const ROWS = 20;
const TICK_MS = 120;

const DIR = {
  UP:    { x: 0, y: -1 },
  DOWN:  { x: 0, y: 1 },
  LEFT:  { x: -1, y: 0 },
  RIGHT: { x: 1, y: 0 },
};

function randomFood(snake) {
  const occupied = new Set(snake.map(s => `${s.x},${s.y}`));
  let x, y;
  do {
    x = Math.floor(Math.random() * COLS);
    y = Math.floor(Math.random() * ROWS);
  } while (occupied.has(`${x},${y}`));
  return { x, y };
}

export default function Snake({ onExit }) {
  const [snake, setSnake] = useState([
    { x: 20, y: 10 },
    { x: 19, y: 10 },
    { x: 18, y: 10 },
  ]);
  const [food, setFood] = useState({ x: 30, y: 10 });
  const [direction, setDirection] = useState(DIR.RIGHT);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [started, setStarted] = useState(false);

  const dirRef = useRef(direction);
  const movedDirRef = useRef(direction);
  const foodRef = useRef(food);
  const gameOverRef = useRef(false);

  useEffect(() => { foodRef.current = food; }, [food]);

  const changeDirection = useCallback((newDir) => {
    // Compare against the direction actually applied last tick, not the last
    // *requested* one — otherwise two quick turns within a single tick can
    // reverse the snake into itself.
    const curr = movedDirRef.current;
    if (curr.x + newDir.x === 0 && curr.y + newDir.y === 0) return;
    dirRef.current = newDir;
    setDirection(newDir);
  }, []);

  const resetGame = useCallback(() => {
    const init = [
      { x: 20, y: 10 },
      { x: 19, y: 10 },
      { x: 18, y: 10 },
    ];
    const nextFood = randomFood(init);
    setSnake(init);
    setFood(nextFood);
    foodRef.current = nextFood;
    setDirection(DIR.RIGHT);
    dirRef.current = DIR.RIGHT;
    movedDirRef.current = DIR.RIGHT;
    setScore(0);
    gameOverRef.current = false;
    setGameOver(false);
    setStarted(true);
  }, []);

  // Keyboard handler
  useEffect(() => {
    const handleKey = (e) => {
      if (gameOverRef.current) {
        if (e.key === 'r' || e.key === 'R') {
          resetGame();
          return;
        }
        onExit();
        return;
      }

      if (!started) {
        if (e.key === 'Escape' || e.key === 'q') {
          onExit();
          return;
        }
        setStarted(true);
        return;
      }

      switch (e.key) {
        case 'ArrowUp':    case 'w': case 'W': changeDirection(DIR.UP);    break;
        case 'ArrowDown':  case 's': case 'S': changeDirection(DIR.DOWN);  break;
        case 'ArrowLeft':  case 'a': case 'A': changeDirection(DIR.LEFT);  break;
        case 'ArrowRight': case 'd': case 'D': changeDirection(DIR.RIGHT); break;
        case 'Escape':     case 'q':           onExit();                   break;
        default: break;
      }
      e.preventDefault();
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [started, onExit, changeDirection, resetGame]);

  // Game loop
  useEffect(() => {
    if (!started || gameOver) return;

    const interval = setInterval(() => {
      setSnake(prev => {
        const dir = dirRef.current;
        movedDirRef.current = dir;
        const head = prev[0];
        const newHead = { x: head.x + dir.x, y: head.y + dir.y };

        // Wall collision
        if (newHead.x < 0 || newHead.x >= COLS || newHead.y < 0 || newHead.y >= ROWS) {
          gameOverRef.current = true;
          setGameOver(true);
          return prev;
        }

        const currentFood = foodRef.current;
        const willGrow = newHead.x === currentFood.x && newHead.y === currentFood.y;

        // Self collision — when not growing, the tail vacates its cell this
        // tick, so exclude it from the check (moving into it is legal).
        const body = willGrow ? prev : prev.slice(0, -1);
        if (body.some((s, i) => i > 0 && s.x === newHead.x && s.y === newHead.y)) {
          gameOverRef.current = true;
          setGameOver(true);
          return prev;
        }

        const newSnake = [newHead, ...prev];

        if (willGrow) {
          setScore(s => s + 10);
          const nextFood = randomFood(newSnake);
          setFood(nextFood);
        } else {
          newSnake.pop();
        }

        return newSnake;
      });
    }, TICK_MS);

    return () => clearInterval(interval);
  }, [started, gameOver]);

  // Render grid
  const renderRow = (y) => {
    const segments = [];
    let emptyRun = '';

    const flush = (key) => {
      if (emptyRun) {
        segments.push(<span key={`e${key}`}>{emptyRun}</span>);
        emptyRun = '';
      }
    };

    for (let x = 0; x < COLS; x++) {
      const isHead = snake[0].x === x && snake[0].y === y;
      const isBody = !isHead && snake.some(s => s.x === x && s.y === y);
      const isFood = food.x === x && food.y === y;

      if (isHead) {
        flush(x);
        segments.push(<span key={`h${x}`} className="snake-head">{'\u2588'}</span>);
      } else if (isBody) {
        flush(x);
        segments.push(<span key={`b${x}`} className="snake-body">{'\u2588'}</span>);
      } else if (isFood) {
        flush(x);
        segments.push(<span key={`f${x}`} className="snake-food">{'\u25CF'}</span>);
      } else {
        emptyRun += ' ';
      }
    }
    flush(COLS);
    return segments;
  };

  const topBorder = '\u250C' + '\u2500'.repeat(COLS) + '\u2510';
  const bottomBorder = '\u2514' + '\u2500'.repeat(COLS) + '\u2518';

  return (
    <div className="snake-container">
      <div className="snake-header">
        <span className="snake-title">snake</span>
        <span className="snake-score">score: {score}</span>
      </div>
      <pre className="snake-grid">
        <span className="snake-border">{topBorder}</span>
        {'\n'}
        {Array.from({ length: ROWS }, (_, y) => (
          <div key={y} className="snake-row">
            <span className="snake-border">{'\u2502'}</span>
            {renderRow(y)}
            <span className="snake-border">{'\u2502'}</span>
          </div>
        ))}
        <span className="snake-border">{bottomBorder}</span>
        {'\n'}
        {gameOver ? (
          <>
            {'\n'}
            <span className="snake-gameover">  game over! score: {score}</span>
            {'\n'}
            <span className="snake-hint">  press r to restart  |  any other key to exit</span>
          </>
        ) : !started ? (
          <>
            {'\n'}
            <span className="snake-hint">  press any key to start  |  wasd/arrows to move  |  q/esc to quit</span>
          </>
        ) : null}
      </pre>
    </div>
  );
}
